import stateApi from '@/api/stateApi';
import Highlight from '@/components/highlight';
import entityIcon from '@/lib/entityIcon';
import navigationEntries from '@/lib/navigationEntries';
import { useRecentEntries } from '@/lib/recentEntities';
import { Group } from '@mantine/core';
import { Spotlight, type SpotlightActionData, type SpotlightActionGroupData } from '@mantine/spotlight';
import { IconSearch } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import id from 'lib/id';
import orderBy from 'lib/orderBy';
import sortDatabases from 'lib/sortDatabases';
import { textMatch, type MatchResult } from 'lib/textMatch';
import { debounce } from 'radash';
import { startTransition, useCallback, useEffectEvent, useMemo, useState, type ReactNode } from 'react';

interface Item extends SpotlightActionData {
  score: number;
}

export default function SearchDialog() {
  const navigate = useNavigate();
  const recentEntries = useRecentEntries();
  const [query, setQuery] = useState('');

  const actions = stateApi.mergedCollections.useStore(
    useCallback(
      (dbs): SpotlightActionGroupData[] => {
        dbs = sortDatabases(dbs, 'name');
        const groups: SpotlightActionGroupData[] = [];
        let match;

        const pageActions: Item[] = [];
        for (const { to: link, icon, label } of navigationEntries) {
          if ((match = textMatch(label, query))) {
            pageActions.push({
              id: link,
              onClick: () => navigate({ to: link }),
              children: <CustomAction icon={icon} match={match} />,
              score: match.score,
            });
          }
        }

        groups.push({
          group: 'Pages',
          actions: orderBy(pageActions, 'score', 'desc'),
        });

        let entityActions: Item[] = [];
        for (const database of dbs) {
          if ((match = textMatch(database.db, query)) && (query ? true : recentEntries.includes(id(database)))) {
            entityActions.push({
              id: id(database),
              onClick: () =>
                navigate({
                  to: '/inspect/$db',
                  params: { db: database.db },
                }),
              children: <CustomAction icon={entityIcon('db', database)} match={match} />,
              score: match.score,
            });
          }

          for (const collection of database.collections) {
            const label = `${database.db}/${collection.coll}`;
            if ((match = textMatch(label, query)) && (query ? true : recentEntries.includes(id(collection)))) {
              entityActions.push({
                id: id(collection),
                onClick: () =>
                  navigate({
                    to: '/inspect/$db/$coll',
                    params: { db: database.db, coll: collection.coll },
                  }),
                children: <CustomAction icon={entityIcon('coll', collection)} match={match} />,
                score: match.score,
              });
            }
          }
        }

        if (query) {
          groups.push({
            group: 'Results',
            actions: orderBy(entityActions, 'score', 'desc').slice(0, 200),
          });
        } else {
          groups.push({
            group: 'Recent',
            actions: orderBy(entityActions, (action) => recentEntries.indexOf(action.id), 'desc').slice(0, 20),
          });
        }

        return groups.filter((group) => group.actions.length > 0);
      },
      [query, navigate, recentEntries],
    ),
  );

  return (
    <CustomSpotlight query={query} setQuery={setQuery}>
      <Spotlight.ActionsList>
        {actions.map((action) => (
          <Spotlight.ActionsGroup key={action.group} label={action.group}>
            {action.actions.map((action) => (
              <Spotlight.Action key={action.id} {...action} />
            ))}
          </Spotlight.ActionsGroup>
        ))}
      </Spotlight.ActionsList>
    </CustomSpotlight>
  );
}

function CustomSpotlight({ query, setQuery, children }: { query: string; setQuery: (query: string) => void; children: ReactNode }) {
  const [localQuery = query, setLocalQuery] = useState<string>();

  const latestSetQuery = useEffectEvent(setQuery);
  const commit = useMemo(
    () =>
      debounce({ delay: 100 }, (query: string) => {
        startTransition(() => {
          setLocalQuery(undefined);
          latestSetQuery(query);
        });
      }),
    [],
  );

  return (
    <Spotlight.Root
      query={localQuery}
      onQueryChange={(query) => {
        setLocalQuery(query);
        commit(query);
      }}
      scrollable
      maxHeight="80vh"
    >
      <Spotlight.Search leftSection={<IconSearch size={20} stroke={1.5} />} placeholder="Search..." />

      {children}
    </Spotlight.Root>
  );
}

function CustomAction({ icon, match }: { icon: ReactNode; match: MatchResult }) {
  return (
    <Group>
      <span
        css={{
          color: 'var(--action-description-color, var(--mantine-color-dimmed))',
          lineHeight: 1,
        }}
      >
        {icon}
      </span>

      <Highlight highlight={match.chunks} />
    </Group>
  );
}
